"use client";

import type { CSSProperties, ReactNode } from "react";

import { cn } from "@homeinstead/ui";
import { useLayoutEffect, useRef, useState } from "react";

type SectionMediaOverlayPin = {
  "--section-media-overlay-left": string;
  "--section-media-overlay-right": string;
  "--section-media-overlay-width": string;
};

type SectionMediaOverlayProps = {
  children: ReactNode;
  /**
   * When set, rating badge entrances inside play on load after this delay
   * instead of waiting for scroll, so they follow the headline accent.
   */
  entranceDelayMilliseconds?: number;
};

function isSamePin(
  current: SectionMediaOverlayPin,
  next: SectionMediaOverlayPin,
): boolean {
  return (
    current["--section-media-overlay-left"] ===
      next["--section-media-overlay-left"] &&
    current["--section-media-overlay-right"] ===
      next["--section-media-overlay-right"] &&
    current["--section-media-overlay-width"] ===
      next["--section-media-overlay-width"]
  );
}

/**
 * Floating rating strips pin to the bottom of the section media: a full-width bar
 * below `md` and the bottom-right corner from `md`. While the media box still
 * extends past the viewport, the strip is fixed to the viewport bottom so it
 * stays in view; once the media bottom catches up, it returns to absolute
 * placement on the media box. The media box, not the section, is measured
 * because stacked media ends before the section does and stops widening at
 * the page's widest breakpoint.
 */
function SectionMediaOverlay({
  children,
  entranceDelayMilliseconds = 0,
}: SectionMediaOverlayProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pin, setPin] = useState<SectionMediaOverlayPin | null>(null);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const mediaBox = panel?.parentElement;

    if (!panel || !mediaBox) {
      return;
    }

    const updatePin = () => {
      const rect = mediaBox.getBoundingClientRect();

      if (rect.bottom <= window.innerHeight) {
        setPin((current) => (current === null ? current : null));
        return;
      }

      const next: SectionMediaOverlayPin = {
        "--section-media-overlay-left": `${Math.round(rect.left)}px`,
        "--section-media-overlay-right": `${Math.round(window.innerWidth - rect.right)}px`,
        "--section-media-overlay-width": `${Math.round(rect.width)}px`,
      };

      setPin((current) =>
        current && isSamePin(current, next) ? current : next,
      );
    };

    updatePin();
    window.addEventListener("scroll", updatePin, { passive: true });
    window.addEventListener("resize", updatePin);

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updatePin);
    observer?.observe(mediaBox);

    return () => {
      window.removeEventListener("scroll", updatePin);
      window.removeEventListener("resize", updatePin);
      observer?.disconnect();
    };
  }, []);

  const isSequenced = entranceDelayMilliseconds > 0;
  const style = {
    ...pin,
    ...(isSequenced
      ? { "--rating-badge-delay": `${entranceDelayMilliseconds}ms` }
      : {}),
  } as CSSProperties;

  return (
    <div
      className={cn(
        // Below md the strip spans the media: the badge stretches with it and
        // keeps its content at the far edge, and the strip carries the tint
        // so the safe-area padding under a pinned badge is tinted too.
        "bottom-0 z-20 flex items-center justify-end max-md:bg-black/50 max-md:*:w-full max-md:[&_[data-rating-floating]]:w-full max-md:[&_[data-rating-floating]]:justify-end max-md:[&_[data-rating-floating]]:bg-transparent",
        pin
          ? "fixed left-[var(--section-media-overlay-left)] w-[var(--section-media-overlay-width)] pb-[env(safe-area-inset-bottom)] md:right-[var(--section-media-overlay-right)] md:left-auto md:w-max md:pb-[max(1rem,env(safe-area-inset-bottom))]"
          : "absolute inset-x-0 md:inset-x-auto md:right-0 md:w-max",
      )}
      data-rating-badge-sequenced={isSequenced ? "" : undefined}
      data-section-media-overlay=""
      data-testid="section-overlay-media-overlay"
      ref={panelRef}
      style={style}
    >
      {children}
    </div>
  );
}

export { SectionMediaOverlay };
